import MojitoPresentation from 'mojito/presentation';
import MojitoCore from 'mojito/core';
import MojitoServices from 'mojito/services';
import BonusesLoadingIndicator from 'modules/bonuses/bonuses-loading-indicator/index.js';
import { useDispatch, useSelector } from 'react-redux';
import { useEffect } from 'react';

const { Image, Text, FlexPane, ProgressBar } = MojitoPresentation.Components;
const CurrencyHelper = MojitoPresentation.Utils.CurrencyHelper;
const NumberUtils = MojitoCore.Base.NumberUtils;
const { BONUS_TYPE, BONUS_FILTER } = MojitoServices.Bonus.types;
const { selectors: campaignSelectors, actions: campaignActions } = MojitoServices.BonusCampaign;
const { selectInprogressBonusCampaigns, selectCampaignsPending } = campaignSelectors;
const { selectCurrency } = MojitoServices.UserInfo.selectors;

const bonusToIconName = {
    [BONUS_TYPE.FREEBET]: 'freeBetIcon',
    [BONUS_TYPE.TOKEN]: 'freeBetIcon',
    [BONUS_TYPE.PREWAGER]: 'prewagerIcon',
    [BONUS_TYPE.CASH]: 'cashIcon',
};

export default function InProgressBonuses(props) {
    const {
        mojitoTools: { config, stringResolver },
    } = props;

    const dispatch = useDispatch();
    useEffect(() => {
        dispatch(campaignActions.getCampaigns(BONUS_FILTER.ACTIVE));
    }, [dispatch]);

    const currencyCode = useSelector(selectCurrency);
    const bonuses = useSelector(selectInprogressBonusCampaigns);
    const campaignsPending = useSelector(selectCampaignsPending);
    const isLoading = campaignsPending && !bonuses.length;

    const renderBonuses = () =>
        bonuses.map(bonus => (
            <InProgressBonus
                key={bonus.id}
                config={config}
                bonus={bonus}
                currencyCode={currencyCode}
                mojitoTools={props.mojitoTools}
            />
        ));

    const renderNoBonusesMessage = () => (
        <Text config={config.noBonusesLabel} class="ta-NoBonusesMessage">
            {stringResolver.resolveString(`$IN_PROGRESS_BONUSES.NO_BONUS_IN_PROGRESS`)}
        </Text>
    );

    return isLoading ? (
        <BonusesLoadingIndicator
            config={config.loadingIndicator}
            labelClassName="ta-fetchingInProgressBonusesText"
        />
    ) : (
        <FlexPane config={config.bonusList} class="ta-InProgressBonuses">
            {bonuses?.length ? renderBonuses() : renderNoBonusesMessage()}
        </FlexPane>
    );
}

function InProgressBonus({ config, mojitoTools, bonus, currencyCode }) {
    const bonusIcon = bonusToIconName[bonus.bonusType];

    return (
        <FlexPane config={config.bonusContainer} class="ta-InProgressBonus">
            <FlexPane config={config.bonusHeader}>
                <FlexPane config={config.bonusNameContainer}>
                    <Image config={config[bonusIcon]} class="ta-InProgressBonusIcon" />
                    <Text config={config.bonusNameLabel} class="ta-InProgressBonusName">
                        {bonus.name}
                    </Text>
                    <Text config={config.bonusPriceLabel} class="ta-InProgressBonusValue">
                        {CurrencyHelper.formatCurrency(bonus.amount, currencyCode)}
                    </Text>
                </FlexPane>
                <Text config={config.bonusDescription} class="ta-BonusDescription">
                    {bonus.description}
                </Text>
            </FlexPane>

            <FlexPane config={config.stepsList}>
                {bonus.steps.map(step => (
                    <BonusStep
                        mojitoTools={mojitoTools}
                        key={step.name}
                        config={config}
                        step={step}
                        currencyCode={currencyCode}
                    />
                ))}
            </FlexPane>
        </FlexPane>
    );
}

function BonusStep({ config, step, currencyCode, mojitoTools }) {
    const { numBets, minBets, amount = 0, requiredWager, status, type } = step;
    const { stringResolver } = mojitoTools;
    const statusMessage = stringResolver
        .resolveString(`$IN_PROGRESS_BONUSES.STATUSES`)
        [status].toLowerCase();

    return (
        <FlexPane config={config.stepContainer} class="ta-BonusStep">
            <FlexPane config={config.stepNameContainer}>
                <FlexPane config={config.stepNumber} class="ta-BonusStepOrder">
                    <Text config={config.stepNumberLabel}>{step.order}</Text>
                </FlexPane>
                <Text config={config.stepNameLabel} class="ta-BonusStepName">
                    {step.name}
                </Text>
                <Text config={config.stepStatus} class="ta-BonusStepStatus">
                    {statusMessage}
                </Text>
            </FlexPane>
            <FlexPane config={config.stepSubContainer}>
                <Text config={config.stepDescription} class="ta-BonusStepDescription">
                    {step.description}
                </Text>
                <FlexPane config={config.progressContainer} class="ta-BonusStep">
                    <BonusProgressBar
                        config={config}
                        current={numBets}
                        max={minBets}
                        label={
                            stringResolver.resolveString(`$IN_PROGRESS_BONUSES.STEP_PROGRESS_TYPE`)[
                                type
                            ].FIRST
                        }
                        bottomLabelFrom={numBets}
                        bottomLabelTo={minBets}
                    />
                    <BonusProgressBar
                        config={config}
                        current={amount}
                        max={requiredWager}
                        label={
                            stringResolver.resolveString(`$IN_PROGRESS_BONUSES.STEP_PROGRESS_TYPE`)[
                                type
                            ].SECOND
                        }
                        bottomLabelFrom={CurrencyHelper.formatCurrency(amount, currencyCode)}
                        bottomLabelTo={CurrencyHelper.formatCurrency(requiredWager, currencyCode)}
                    />
                </FlexPane>
            </FlexPane>
        </FlexPane>
    );
}

function BonusProgressBar({ config, current, max, label, bottomLabelFrom, bottomLabelTo }) {
    if (current !== undefined && max !== undefined) {
        const progressRatio = Math.min(NumberUtils.numberToPrecision(current / max, 2), 1);

        return (
            <div>
                <ProgressBar
                    config={config.progressBar}
                    progress={progressRatio}
                    progressLabel={label}
                />
                <Text
                    config={config.stepInfo}
                    class="ta-BonusStepProgressInfo"
                >{`${bottomLabelFrom} / ${bottomLabelTo}`}</Text>
            </div>
        );
    }
}
